import { Component, OnDestroy, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";

import { getHomeMenuWithVisited } from "src/app/_core/constants/menu.constants";
import {
	PerfilViewModel,
	PoliticaViewModel,
} from "src/app/_core/models/admin/perfil.model";
import { EmpleadoViewModel } from "src/app/_core/models/centros/empleado.model";
import { GlobalService } from "src/app/_core/providers/global.service";
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";
import { PerfilService } from "src/app/_core/providers/perfil.service";
import { BreadcrumbService } from "src/app/_core/services/breadcrumb.service";

@Component({
	selector: "app-roles-page",
	templateUrl: "./roles.page.html",
	styleUrls: ["./roles.page.scss"],
	standalone: false,
})
export class RolesPageComponent implements OnInit, OnDestroy {
	private readonly menu = getHomeMenuWithVisited("/admin");
	private readonly destroy$ = new Subject<void>();

	perfiles: PerfilViewModel[] = [];
	politicas: PoliticaViewModel[] = [];

	// --- Asignación de perfiles a un usuario ---
	userList: EmpleadoViewModel[] = [];
	selectedUser: EmpleadoViewModel | null = null;
	userPerfiles: PerfilViewModel[] = [];
	perfilToAssign: number | null = null;

	// --- Editor de perfil ---
	editPerfil: PerfilViewModel | null = null;
	isNew = false;

	/** Etiqueta de opción para app-ds-select (catálogo de perfiles asignables). */
	descripcionOf = (p: { descripcion?: string } | null): string =>
		p?.descripcion ?? "";

	constructor(
		private readonly _titleService: Title,
		private readonly _translateService: TranslateService,
		private readonly _globalService: GlobalService,
		private readonly _breadcrumbService: BreadcrumbService,
		private readonly _perfilService: PerfilService,
		private readonly _masterService: MasterService,
		private readonly _notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		this._globalService.setHeaderOptions([], null, [], this.menu);
		this._titleService.setTitle(
			this._translateService.instant("roles.title"),
		);
		this._breadcrumbService.setDetail(
			this._translateService.instant("roles.title"),
		);

		this._loadPerfiles();
		this._loadPoliticas();
		this._loadEmpleados();
	}

	ngOnDestroy(): void {
		this._breadcrumbService.clear();
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ---------- carga ----------
	private _loadPerfiles(): void {
		this._perfilService
			.getPerfiles()
			.pipe(takeUntil(this.destroy$))
			.subscribe((r) => (this.perfiles = r ?? []));
	}

	private _loadPoliticas(): void {
		this._perfilService
			.getPoliticas()
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(r) =>
					(this.politicas = (r ?? []).sort((a, b) =>
						a.codigo.localeCompare(b.codigo),
					)),
			);
	}

	private _loadEmpleados(): void {
		this._masterService
			.getEmpleados()
			.pipe(takeUntil(this.destroy$))
			.subscribe((r) => (this.userList = r ?? []));
	}

	// ---------- asignación a usuario ----------
	onUserSelected(user: EmpleadoViewModel | null): void {
		this.selectedUser = user;
		this.perfilToAssign = null;
		this._loadUserPerfiles();
	}

	private _loadUserPerfiles(): void {
		if (!this.selectedUser) {
			this.userPerfiles = [];
			return;
		}
		this._perfilService
			.getPerfilesByUsuario(this.selectedUser.id)
			.pipe(takeUntil(this.destroy$))
			.subscribe((r) => (this.userPerfiles = r ?? []));
	}

	get perfilesAsignables(): PerfilViewModel[] {
		const asignados = new Set(this.userPerfiles.map((p) => p.id));
		return this.perfiles.filter((p) => p.activo && !asignados.has(p.id));
	}

	addPerfil(): void {
		if (!this.selectedUser || !this.perfilToAssign) {
			return;
		}
		this._perfilService
			.asignarPerfil(this.selectedUser.id, this.perfilToAssign)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.perfilToAssign = null;
					this._loadUserPerfiles();
					this._notifyOk();
				},
				error: () => this._notifyError(),
			});
	}

	removePerfil(perfilId: number): void {
		if (!this.selectedUser) {
			return;
		}
		this._perfilService
			.quitarPerfil(this.selectedUser.id, perfilId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this._loadUserPerfiles();
					this._notifyOk();
				},
				error: () => this._notifyError(),
			});
	}

	// ---------- editor de perfil ----------
	newPerfil(): void {
		this.isNew = true;
		this.editPerfil = {
			id: 0,
			codigo: "",
			descripcion: "",
			necesitaScope: true,
			activo: true,
			policies: [],
		};
	}

	editExisting(p: PerfilViewModel): void {
		this.isNew = false;
		this.editPerfil = { ...p, policies: [...(p.policies ?? [])] };
	}

	cancelEdit(): void {
		this.editPerfil = null;
	}

	isPolicySelected(codigo: string): boolean {
		return !!this.editPerfil && this.editPerfil.policies.includes(codigo);
	}

	togglePolicy(codigo: string, checked: boolean): void {
		if (!this.editPerfil) {
			return;
		}
		if (checked) {
			if (!this.editPerfil.policies.includes(codigo)) {
				this.editPerfil.policies.push(codigo);
			}
		} else {
			this.editPerfil.policies = this.editPerfil.policies.filter(
				(c) => c !== codigo,
			);
		}
	}

	get canSavePerfil(): boolean {
		return (
			!!this.editPerfil &&
			!!this.editPerfil.codigo?.trim() &&
			!!this.editPerfil.descripcion?.trim()
		);
	}

	savePerfil(): void {
		if (!this.editPerfil || !this.canSavePerfil) {
			return;
		}
		const obs = this.isNew
			? this._perfilService.crearPerfil(this.editPerfil)
			: this._perfilService.actualizarPerfil(this.editPerfil);

		obs.pipe(takeUntil(this.destroy$)).subscribe({
			next: () => {
				this.editPerfil = null;
				this._loadPerfiles();
				if (this.selectedUser) {
					this._loadUserPerfiles();
				}
				this._notifyOk();
			},
			error: () => this._notifyError(),
		});
	}

	private _notifyOk(): void {
		this._notificationService.showSuccess(
			this._translateService.instant("messages.savedSuccessfully"),
			null,
		);
	}

	private _notifyError(): void {
		this._notificationService.showError(
			this._translateService.instant("messages.errorsWhileSaving"),
		);
	}
}
